import { http } from '@/http/axios';
import { postAnalyticsEvent } from '@/http/analyticsIngestApi';
import {
  postAttendeeChatMessage,
  postChatReaction,
  submitInteraction,
} from '@/http/interactionWriteApi';
import { primeAttendeeProfileCookie } from '@/http/attendeeProfileCookieApi';
import { getPublicRead, getPublicReadResponse } from '@/http/publicReadApi';
import {
  OFFER_ASSIGNMENT_RETRY_DELAYS_MS,
  PROFILE_COOKIE_MAX_WAIT_MS,
  waitAtMost,
} from '@/lib/publicOfferRead';
import type { StaffAccessLinkResolution } from '@/lib/staffAccessLink';
import type {
  AnalyticsEventInput,
  LiveKitLayoutInput,
  LiveKitLayoutUpdateResult,
  ChatHistoryPage,
  ChatMessageDto,
  ChatToken,
  GetSessionPresenceParams,
  LivekitParticipant,
  LiveSessionResourceStateDto,
  PlaybackInfo,
  LiveSessionControl,
  PostChatMessageInput,
  PostChatReactionInput,
  PostLiveReactionInput,
  ModeratorToken,
  PresenterToken,
  RoleToken,
  RotatedInviteToken,
  SessionPresenceDto,
  SpeakerToken,
  TriggerEventInput,
  ViewerToken,
  WebinarEventHistoryDto,
  WebinarPresenceStatusDto,
  WebinarPurchaseNotificationConfig,
  WebinarPurchaseNotificationConfigInput,
} from '@/features/live/types';
import type {
  EvaluationSubmissionInput,
  EvaluationTemplate,
  OfferTemplate,
  StripeEmbeddedCheckoutDto,
  OfferResults,
  ParticipationStatusDto,
  PublicEvergreenChatConfiguration,
  PublicSurveySubmission,
  PublicWebinarMe,
  PublicWebinarSession,
  QuitPage,
  QuizResults,
  QuizSubmission,
  QuizTemplate,
  SurveyResults,
  SurveyTemplate,
  ThankYouPage,
  WebinarResource,
  WebinarSlide,
} from '@/features/webinar/types';
import type { WorkspaceSocialMediaLink } from '@/features/workspace/types';
import type { EvergreenChatModeOrigin, LiveChatMode } from '@/features/live/ablySessionChannels';

type EvergreenSessionChatMode = {
  chatMode: LiveChatMode;
  origin?: EvergreenChatModeOrigin;
  revision?: number;
};

/** Fusionne les appels concurrents (Redux + Ably authCallback + fallback workspace) en une seule requête HTTP. */
const inflightAdminChatToken = new Map<string, Promise<ChatToken>>();
const inflightPublicChatToken = new Map<string, Promise<ChatToken>>();

function dedupeChatTokenRequest(
  cacheKey: string,
  cache: Map<string, Promise<ChatToken>>,
  fetcher: () => Promise<ChatToken>,
): Promise<ChatToken> {
  const existing = cache.get(cacheKey);
  if (existing) {
    return existing;
  }
  const promise = fetcher().finally(() => {
    queueMicrotask(() => {
      cache.delete(cacheKey);
    });
  });
  cache.set(cacheKey, promise);
  return promise;
}

export const liveService = {
  getViewerToken: (webinarId: number, sessionIdentifier: string): Promise<ViewerToken> =>
    http
      .get<ViewerToken>(`/public/webinars/${webinarId}/sessions/${sessionIdentifier}/viewer-token`)
      .then((r) => r.data),
  getRoleToken: (
    webinarId: number,
    sessionIdentifier: string,
    inviteToken: string,
  ): Promise<RoleToken> =>
    http
      .post<RoleToken>(`/public/webinars/${webinarId}/sessions/${sessionIdentifier}/role-token`, {
        inviteToken,
      })
      .then((r) => r.data),
  getAttendeeInfo: (webinarId: number, authorization?: string): Promise<PublicWebinarMe> =>
    http
      .get<PublicWebinarMe>(`/public/webinars/${webinarId}/me`, {
        headers: authorization ? { Authorization: authorization } : undefined,
      })
      .then((r) => r.data),
  getChatHistory: (
    webinarId: number,
    sessionIdentifier: string,
    authorization: string,
    page = 0,
  ): Promise<ChatHistoryPage> =>
    getPublicReadResponse<ChatMessageDto[]>(
      `/public/webinars/${webinarId}/sessions/${sessionIdentifier}/chat`,
      { authorization, params: { page } },
    ).then((r) => ({
      messages: Array.isArray(r.data) ? r.data : [],
      hasNext: String(r.headers['x-has-next']).toLowerCase() === 'true',
    })),
  postChatReaction: (
    webinarId: number,
    sessionIdentifier: string,
    authorization: string,
    input: PostChatReactionInput,
  ): Promise<void> => postChatReaction(webinarId, sessionIdentifier, authorization, input),
  postLiveReaction: (
    webinarId: number,
    sessionIdentifier: string,
    authorization: string,
    input: PostLiveReactionInput,
  ): Promise<void> =>
    http
      .post(`/public/webinars/${webinarId}/sessions/${sessionIdentifier}/live/reactions`, input, {
        headers: { Authorization: authorization },
      })
      .then(() => undefined),
  postAttendeeChatMessage: (
    webinarId: number,
    sessionIdentifier: string,
    authorization: string,
    input: PostChatMessageInput,
    commandId?: string,
  ): Promise<ChatMessageDto | null> =>
    postAttendeeChatMessage(webinarId, sessionIdentifier, authorization, input, commandId),

  pinChatMessage: (
    webinarId: number,
    sessionIdentifier: string,
    messageId: number,
    authorization: string,
  ): Promise<void> =>
    http
      .post(
        `/public/webinars/${webinarId}/sessions/${sessionIdentifier}/chat/messages/${messageId}/pin`,
        undefined,
        {
          headers: { Authorization: authorization },
        },
      )
      .then(() => undefined),
  unpinChatMessage: (
    webinarId: number,
    sessionIdentifier: string,
    messageId: number,
    authorization: string,
  ): Promise<void> =>
    http
      .delete(
        `/public/webinars/${webinarId}/sessions/${sessionIdentifier}/chat/messages/${messageId}/pin`,
        {
          headers: { Authorization: authorization },
        },
      )
      .then(() => undefined),

  deleteChatMessage: (
    webinarId: number,
    sessionIdentifier: string,
    messageId: number,
    authorization: string,
  ): Promise<void> =>
    http
      .delete(
        `/public/webinars/${webinarId}/sessions/${sessionIdentifier}/chat/messages/${messageId}`,
        {
          headers: { Authorization: authorization },
        },
      )
      .then(() => undefined),

  startHlsEgress: (workspaceId: number, webinarId: number, sessionId: number): Promise<string> =>
    http
      .post<string>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/livekit/egress/hls:start`,
      )
      .then((r) => r.data),

  startMp4Egress: (workspaceId: number, webinarId: number, sessionId: number): Promise<string> =>
    http
      .post<string>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/livekit/egress/mp4:start`,
      )
      .then((r) => r.data),

  stopEgress: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
    egressId: string,
  ): Promise<void> =>
    http
      .post(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/livekit/egress/${egressId}:stop`,
      )
      .then(() => undefined),

  startCustomRecording: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
  ): Promise<LiveSessionControl> =>
    http
      .post<LiveSessionControl>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/recording/start-custom`,
      )
      .then((r) => r.data),

  resetSessionResources: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
  ): Promise<void> =>
    http
      .post(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/resources/reset`,
      )
      .then(() => undefined),

  /** `applied` est faux sur un 204 : la room LiveKit n'existe pas encore, rien n'a été écrit. */
  updateSessionLayout: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
    input: LiveKitLayoutInput,
  ): Promise<LiveKitLayoutUpdateResult> =>
    http
      .post(`/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/layout`, input)
      .then((response) => ({ applied: response.status !== 204 })),

  getWorkspaceChatMessages: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
  ): Promise<ChatMessageDto[]> =>
    http
      .get<ChatMessageDto[]>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/chat/messages`,
      )
      .then((r) => r.data),

  getWorkspaceQuestions: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
    params?: { page?: number; size?: number },
  ): Promise<{
    content: ChatMessageDto[];
    last: boolean;
    number: number;
    totalPages: number;
  }> =>
    http
      .get<{
        content: ChatMessageDto[];
        last: boolean;
        number: number;
        totalPages: number;
      }>(`/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/chat/questions`, {
        params,
      })
      .then((r) => r.data),

  postWorkspaceChatMessage: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
    input: PostChatMessageInput,
  ): Promise<ChatMessageDto> =>
    http
      .post<ChatMessageDto>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/chat/messages`,
        input,
      )
      .then((r) => r.data),

  pinWorkspaceChatMessage: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
    messageId: number,
  ): Promise<void> =>
    http
      .post(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/chat/messages/${messageId}/pin`,
      )
      .then(() => undefined),

  updateEvergreenSessionChatMode: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
    chatMode: LiveChatMode,
  ): Promise<EvergreenSessionChatMode> =>
    http
      .put<EvergreenSessionChatMode>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/chat/mode`,
        { chatMode },
      )
      .then((r) => r.data),

  unpinWorkspaceChatMessage: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
    messageId: number,
  ): Promise<void> =>
    http
      .delete(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/chat/messages/${messageId}/pin`,
      )
      .then(() => undefined),

  deleteWorkspaceChatMessage: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
    messageId: number,
  ): Promise<void> =>
    http
      .delete(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/chat/messages/${messageId}`,
      )
      .then(() => undefined),

  moveWorkspaceMessageToQuestions: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
    messageId: number,
  ): Promise<ChatMessageDto> =>
    http
      .post<ChatMessageDto>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/chat/messages/${messageId}/question`,
      )
      .then((r) => r.data),

  validateWorkspaceQuestion: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
    messageId: number,
  ): Promise<ChatMessageDto> =>
    http
      .post<ChatMessageDto>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/chat/questions/${messageId}/validate`,
      )
      .then((r) => r.data),

  triggerEvent: (
    webinarId: number,
    sessionIdentifier: string,
    input: TriggerEventInput,
    authorization?: string,
  ): Promise<void> =>
    http
      .post(`/public/webinars/${webinarId}/sessions/${sessionIdentifier}/events/trigger`, input, {
        headers: authorization ? { Authorization: authorization } : undefined,
      })
      .then(() => undefined),

  resolveStaffAccessLink: (
    webinarId: number,
    sessionId: number,
    shortIdentifier: string,
  ): Promise<StaffAccessLinkResolution> =>
    http
      .get<StaffAccessLinkResolution>(
        `/admin-live/${webinarId}/${sessionId}/access-links/${shortIdentifier}`,
      )
      .then((r) => r.data),

  getAdminChatToken: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
  ): Promise<ChatToken> =>
    dedupeChatTokenRequest(
      `admin:${workspaceId}:${webinarId}:${sessionId}`,
      inflightAdminChatToken,
      () =>
        http
          .get<ChatToken>(
            `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/chat/token`,
          )
          .then((r) => r.data),
    ),

  getPublicChatToken: (
    webinarId: number,
    sessionIdentifier: string,
    authorization?: string,
  ): Promise<ChatToken> =>
    dedupeChatTokenRequest(
      `public:${webinarId}:${sessionIdentifier}:${authorization ?? ''}`,
      inflightPublicChatToken,
      () =>
        getPublicRead<ChatToken>(
          `/public/webinars/${webinarId}/sessions/${sessionIdentifier}/chat/token`,
          { authorization },
        ),
    ),

  startSession: (workspaceId: number, webinarId: number, sessionId: number): Promise<void> =>
    http
      .post(`/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/start`)
      .then(() => undefined),

  getPresenterToken: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
  ): Promise<PresenterToken> =>
    http
      .post<PresenterToken>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/livekit/presenter-token`,
      )
      .then((r) => r.data),

  getModeratorToken: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
  ): Promise<ModeratorToken> =>
    http
      .post<ModeratorToken>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/livekit/moderator-token`,
      )
      .then((r) => r.data),

  recordStaffPresence: (workspaceId: number, webinarId: number, sessionId: number): Promise<void> =>
    http
      .post(`/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/staff-presence`)
      .then(() => undefined),

  getSpeakerTokens: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
  ): Promise<SpeakerToken[]> =>
    http
      .post<SpeakerToken[]>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/livekit/speaker-token`,
      )
      .then((r) => r.data),

  closeSession: (workspaceId: number, webinarId: number, sessionId: number): Promise<void> =>
    http
      .post(`/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/stop`)
      .then(() => undefined),

  redirectLiveSession: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
    criticalBackupUrl: string,
  ): Promise<void> =>
    http
      .post(`/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/redirect`, {
        criticalBackupUrl,
      })
      .then(() => undefined),

  muteTrack: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
    identity: string,
    trackSid: string,
    muted: boolean,
  ): Promise<void> =>
    http
      .post(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/livekit/participants/${identity}/tracks/${trackSid}/mute`,
        { muted },
      )
      .then(() => undefined),

  getPlayback: (workspaceId: number, webinarId: number, sessionId: number): Promise<PlaybackInfo> =>
    http
      .get<PlaybackInfo>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/livekit/playback`,
      )
      .then((r) => r.data),

  getPublicPlayback: (
    webinarId: number,
    sessionIdentifier: string,
    authorization?: string,
  ): Promise<PlaybackInfo> =>
    getPublicRead<PlaybackInfo>(
      `/public/webinars/${webinarId}/sessions/${sessionIdentifier}/playback`,
      { authorization },
    ),

  getPublicSession: (webinarId: number, sessionIdentifier: string): Promise<PublicWebinarSession> =>
    getPublicRead<PublicWebinarSession>(
      `/public/webinars/${webinarId}/sessions/${sessionIdentifier}`,
    ),

  getPublicEvergreen: (
    webinarId: number,
    sessionIdentifier: string,
  ): Promise<PublicEvergreenChatConfiguration> =>
    getPublicRead<PublicEvergreenChatConfiguration>(
      `/public/webinars/${webinarId}/sessions/${sessionIdentifier}/evergreen`,
    ),

  getPresenceStatus: (
    webinarId: number,
    sessionIdentifier: string,
    authorization: string,
  ): Promise<WebinarPresenceStatusDto> =>
    getPublicRead<WebinarPresenceStatusDto>(
      `/public/webinars/${webinarId}/sessions/${sessionIdentifier}/presence-status`,
      { authorization },
    ),

  getPublicResources: (
    webinarId: number,
    sessionIdentifier: string,
    authorization?: string,
  ): Promise<LiveSessionResourceStateDto[]> =>
    getPublicRead<LiveSessionResourceStateDto[]>(
      `/public/webinars/${webinarId}/sessions/${sessionIdentifier}/resources/state`,
      { authorization },
    ),

  getPublicOffer: (
    webinarId: number,
    offerId: number,
    authorization?: string,
  ): Promise<OfferTemplate> => {
    const profileCookie = authorization
      ? waitAtMost(primeAttendeeProfileCookie(authorization), PROFILE_COOKIE_MAX_WAIT_MS)
      : Promise.resolve();
    return profileCookie.then(() =>
      getPublicRead<OfferTemplate>(`/public/webinars/${webinarId}/offers/${offerId}`, {
        authorization,
        canary: 'offer',
        liveApiRetryDelaysMs: OFFER_ASSIGNMENT_RETRY_DELAYS_MS,
      }),
    );
  },

  primeAttendeeProfileCookie: (authorization: string): Promise<void> =>
    primeAttendeeProfileCookie(authorization),

  // Écriture côté welya-api (réservation + appel Stripe) : jamais servie par le live-api.
  // Sans formule choisie, le back prend la première de l'offre.
  createStripeCheckoutSession: (
    webinarId: number,
    offerId: number,
    authorization: string,
    priceId: string | null,
    signal?: AbortSignal,
  ): Promise<StripeEmbeddedCheckoutDto> =>
    http
      .post<StripeEmbeddedCheckoutDto>(
        `/public/webinars/${webinarId}/offers/${offerId}/stripe-checkout-sessions`,
        priceId ? { priceId } : null,
        { headers: { Authorization: authorization }, signal },
      )
      .then((r) => r.data),

  getAdminResourceState: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
  ): Promise<LiveSessionResourceStateDto[]> =>
    http
      .get<LiveSessionResourceStateDto[]>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/resources`,
      )
      .then((r) => r.data),

  triggerAdminEvent: (
    workspaceId: number,
    webinarId: number,
    sessionIdentifier: string,
    input: TriggerEventInput,
  ): Promise<void> =>
    http
      .post(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionIdentifier}/events/trigger`,
        input,
      )
      .then(() => undefined),

  removeParticipant: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
    identity: string,
  ): Promise<void> =>
    http
      .delete(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/livekit/participants/${identity}`,
      )
      .then(() => undefined),

  rotateInviteToken: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
    inviteId: number,
  ): Promise<RotatedInviteToken> =>
    http
      .post<RotatedInviteToken>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/invites/sessions/${sessionId}/invites/${inviteId}/rotate`,
      )
      .then((r) => r.data),

  getLivekitParticipants: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
  ): Promise<LivekitParticipant[]> =>
    http
      .get<LivekitParticipant[]>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/livekit/participants`,
      )
      .then((r) => r.data),

  getSessionPresence: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
    params?: GetSessionPresenceParams,
  ): Promise<SessionPresenceDto> =>
    http
      .get<SessionPresenceDto>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/presence`,
        { params },
      )
      .then((r) => r.data),

  banPresenceParticipant: (
    workspaceId: number,
    webinarId: number,
    sessionId: number,
    subscriptionId: number,
  ): Promise<void> =>
    http
      .post(
        `/workspaces/${workspaceId}/webinars/${webinarId}/sessions/${sessionId}/presence/participants/${subscriptionId}/ban`,
      )
      .then(() => undefined),

  postAnalyticsEvent: (
    webinarId: number,
    sessionIdentifier: string,
    authorization: string,
    input: AnalyticsEventInput,
  ): Promise<void> => postAnalyticsEvent(webinarId, sessionIdentifier, authorization, input),

  getPublicSurvey: (
    webinarId: number,
    surveyId: number,
    authorization: string,
  ): Promise<SurveyTemplate> =>
    getPublicRead<SurveyTemplate>(`/public/webinars/${webinarId}/surveys/${surveyId}`, {
      authorization,
    }),

  getPublicQuiz: (
    webinarId: number,
    quizId: number,
    authorization: string,
  ): Promise<QuizTemplate> =>
    getPublicRead<QuizTemplate>(`/public/webinars/${webinarId}/quizzes/${quizId}`, {
      authorization,
    }),

  getPublicQuitPage: (webinarId: number, authorization: string): Promise<QuitPage> =>
    getPublicRead<QuitPage>(`/public/webinars/${webinarId}/quit-page`, { authorization }),

  submitPublicQuitPage: (
    webinarId: number,
    authorization: string,
    input: {
      sessionId: number;
      reasonIds: number[];
      otherReasonText?: string | null;
    },
  ): Promise<void> =>
    http
      .post<void>(`/public/webinars/${webinarId}/quit-page/submit`, input, {
        headers: { Authorization: authorization },
      })
      .then(() => undefined),

  submitPublicSurvey: (
    webinarId: number,
    templateId: number,
    authorization: string,
    input: {
      sessionId: number;
      answers: Array<{
        questionId: number;
        optionIds: number[];
        freeText: string | null;
      }>;
    },
  ): Promise<PublicSurveySubmission | null> =>
    submitInteraction('survey', 'post', webinarId, templateId, authorization, input),

  submitPublicSurveyPut: (
    webinarId: number,
    templateId: number,
    authorization: string,
    input: {
      sessionId: number;
      answers: Array<{
        questionId: number;
        optionIds: number[];
        freeText: string | null;
      }>;
    },
  ): Promise<PublicSurveySubmission | null> =>
    submitInteraction('survey', 'put', webinarId, templateId, authorization, input),

  getSurveyResults: (
    workspaceId: number,
    webinarId: number,
    templateId: number,
    sessionId?: number,
  ): Promise<SurveyResults> =>
    http
      .get<SurveyResults>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/surveys/${templateId}/results`,
        sessionId != null ? { params: { sessionId } } : undefined,
      )
      .then((r) => r.data),

  getQuizResults: (
    workspaceId: number,
    webinarId: number,
    templateId: number,
    sessionId?: number,
  ): Promise<QuizResults> =>
    http
      .get<QuizResults>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/quizzes/${templateId}/results`,
        sessionId != null ? { params: { sessionId } } : undefined,
      )
      .then((r) => r.data),

  getOfferResults: (
    workspaceId: number,
    webinarId: number,
    offerId: number,
    sessionId?: number,
  ): Promise<OfferResults> =>
    http
      .get<OfferResults>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/offers/${offerId}/results`,
        sessionId != null ? { params: { sessionId } } : undefined,
      )
      .then((r) => r.data),

  submitPublicQuiz: (
    webinarId: number,
    templateId: number,
    authorization: string,
    input: {
      sessionId: number;
      answers: Array<{
        questionId: number;
        optionIds: number[];
      }>;
    },
  ): Promise<QuizSubmission | null> =>
    submitInteraction('quiz', 'post', webinarId, templateId, authorization, input),

  submitPublicQuizPut: (
    webinarId: number,
    templateId: number,
    authorization: string,
    input: {
      sessionId: number;
      answers: Array<{
        questionId: number;
        optionIds: number[];
      }>;
    },
  ): Promise<QuizSubmission | null> =>
    submitInteraction('quiz', 'put', webinarId, templateId, authorization, input),

  getMyPublicQuizSubmission: (
    webinarId: number,
    templateId: number,
    authorization: string,
  ): Promise<QuizSubmission | null> =>
    http
      .get<QuizSubmission | null>(
        `/public/webinars/${webinarId}/quizzes/${templateId}/my-submission`,
        {
          headers: { Authorization: authorization },
        },
      )
      .then((r) => r.data),

  getPublicSlides: (
    webinarId: number,
    authorization: string,
    params?: { page?: number; size?: number },
  ): Promise<{
    content: WebinarSlide[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
  }> =>
    http
      .get<{
        content: WebinarSlide[];
        totalElements: number;
        totalPages: number;
        size: number;
        number: number;
      }>(`/public/webinars/${webinarId}/slides`, {
        headers: { Authorization: authorization },
        params,
      })
      .then((r) => r.data),

  getPublicSlide: (
    webinarId: number,
    slideId: number,
    authorization: string,
  ): Promise<WebinarSlide> =>
    http
      .get<WebinarSlide>(`/public/webinars/${webinarId}/slides/${slideId}`, {
        headers: { Authorization: authorization },
      })
      .then((r) => r.data),

  getPublicWebinarResources: (
    webinarId: number,
    authorization: string,
    params?: { page?: number; size?: number },
  ): Promise<{
    content: WebinarResource[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
  }> =>
    getPublicRead<{
      content: WebinarResource[];
      totalElements: number;
      totalPages: number;
      size: number;
      number: number;
    }>(`/public/webinars/${webinarId}/resources`, { authorization, params }),
  getPublicResource: (
    webinarId: number,
    resourceId: number,
    authorization: string,
  ): Promise<WebinarResource> =>
    getPublicRead<WebinarResource>(`/public/webinars/${webinarId}/resources/${resourceId}`, {
      authorization,
    }),
  getPublicWorkspaceSocialMediaLinks: (workspaceId: number): Promise<WorkspaceSocialMediaLink[]> =>
    getPublicRead<WorkspaceSocialMediaLink[]>(
      `/public/workspaces/${workspaceId}/social-media-links`,
    ).catch(() => []),
  getOfferPurchaseNotificationConfig: (
    workspaceId: number,
    webinarId: number,
    offerId: number,
  ): Promise<WebinarPurchaseNotificationConfig> =>
    http
      .get<WebinarPurchaseNotificationConfig>(
        `/workspaces/${workspaceId}/webinars/${webinarId}/offers/${offerId}/purchase-notifications`,
      )
      .then((r) => r.data),
  updateOfferPurchaseNotificationConfig: (
    workspaceId: number,
    webinarId: number,
    offerId: number,
    input: WebinarPurchaseNotificationConfigInput,
  ): Promise<void> =>
    http
      .put(
        `/workspaces/${workspaceId}/webinars/${webinarId}/offers/${offerId}/purchase-notifications`,
        input,
      )
      .then(() => undefined),

  submitPublicEvaluation: (
    webinarId: number,
    authorization: string,
    input: EvaluationSubmissionInput,
  ): Promise<void> =>
    http
      .post(`/public/webinars/${webinarId}/evaluation/submit`, input, {
        headers: { Authorization: authorization },
      })
      .then(() => undefined),

  getPublicEvaluation: (webinarId: number, authorization: string): Promise<EvaluationTemplate> =>
    getPublicRead<EvaluationTemplate>(`/public/webinars/${webinarId}/evaluation`, {
      authorization,
    }),

  getPublicThankYouPage: (webinarId: number, authorization: string): Promise<ThankYouPage> =>
    getPublicRead<ThankYouPage>(`/public/webinars/${webinarId}/thank-you-page`, {
      authorization,
    }),

  getPublicParticipationStatuses: (
    webinarId: number,
    authorization: string,
  ): Promise<ParticipationStatusDto[]> =>
    http
      .get<ParticipationStatusDto[]>(`/public/webinars/${webinarId}/participation-statuses`, {
        headers: { Authorization: authorization },
      })
      .then((r) => r.data),

  getEventHistory: (
    webinarId: number,
    sessionIdentifier: string,
    authorization?: string,
  ): Promise<WebinarEventHistoryDto> =>
    http
      .get<WebinarEventHistoryDto>(
        `/public/webinars/${webinarId}/sessions/${sessionIdentifier}/event-history`,
        {
          headers: { Authorization: authorization },
        },
      )
      .then((r) => r.data),
};
